<template>
  <div class="d-flex flex-column">
    <!-- SHOW -->
    <div v-if="image.id" class="image-container">
      <img :src="image.url" class="image w-100" :width="imgWidth" :height="imgHeight" />
      <div class="delete d-flex bg-danger" @click="deleteImage()">
        <i class="flaticon-cancel m-auto p-0" style="line-height: 0" />
      </div>
      <div v-if="showSettings && image.settings" class="p-3">
        <label class="font-weight-bold">Настройки</label>
        <div>
          <div class="d-flex justify-content-between mb-2">
            <label>Подпись
              <b-form-input
                v-model="image.settings.label"
                type="text"
                size="sm"
                trim
              />
            </label>
            <label>Альтернативный текст
              <button id="image-alt" class="ml-1">
                <i class="flaticon-info pr-0 text-primary" />
              </button>
              <b-popover target="image-alt" triggers="focus" placement="top">
                Текст, который отобразится вместо фото, если оно не загрузится.
              </b-popover>
              <b-form-input
                v-model="image.settings.alt"
                type="text"
                size="sm"
                trim
              />
            </label>
          </div>
          <div class="d-flex justify-content-between">
            <label>Ширина
              <b-form-input
                v-model="image.settings.width"
                type="number"
                size="sm"
                trim
              />
            </label>
            <label>Высота
              <b-form-input
                v-model="image.settings.height"
                type="number"
                size="sm"
                trim
              />
            </label>
          </div>
        </div>
      </div>
    </div>
    <!-- UPLOAD -->
    <div v-else class="p-5">
      <input
        type="file"
        accept=".jpeg, .jpg, .png"
        ref="actualImageInput"
        class="d-none"
        @change="(e) => uploadBrowsedImage(e)"
      />
      <div
        class="image-upload"
        @click="chooseImage()"
        @dragover="(e) => onDragover(e)"
        @dragleave="(e) => onDragLeave(e)"
        @drop="(e) => uploadDroppedImage(e)"
      >
        <div class="banner">
          <b-icon-cloud-arrow-up class="upload-icon" />
          <span class="prompt">Перетащите изображение сюда или нажмите, чтобы открыть диалог загрузки</span>
        </div>
      </div>
    </div>
    <CropUploadedImage
      :cropBoxRatio="cropBoxRatio"
      :previewImgSrc="previewImgSrc"
      ref="CropUploadedImage"
      @uploadImage="uploadImage"
    />
  </div>
</template>

<style lang="scss" scoped>
.image-container {
  position: relative;
  .image {
    border-radius: 0px 0px 5px 5px;
  }

  .delete {
    border-radius: 0px 0px 0px 2px;
    border-left: 1px solid white;
    border-bottom: 1px solid white;
    position: absolute;
    top: 0px;
    right: 0px;
    width: 24px;
    height: 24px;
    color: white;
    cursor: pointer;
    * {
      cursor: pointer;
    }
    &:hover {
      filter: brightness(95%);
    }
  }
}

.image-upload {
  cursor: pointer;
  width: 100%;
  height: 300px;
  border: 2px dashed #adadad;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  &.drag-over {
    background-color: #eee;
    border-color: #3699ff;
  }
  .banner {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    pointer-events: none;
    .upload-icon {
      width: 100px;
      height: 100px;
      color: #e0e0e0;
    }
    .prompt {
      font-size: 1rem;
      font-weight: 500;
      max-width: 250px;
      text-align: center;
      color: #3f4254;
    }
  }
}
</style>

<script>
import Api from "shared/service/api/api";
import { PREVENT_UNLOAD, ALLOW_UNLOAD } from "shared/store/modules/beforeunload.store.js";
import { BIconCloudArrowUp } from "bootstrap-vue";
import CropUploadedImage from "shared/widgets/upload/image/CropUploadedImage.vue";

export default {
  name: "CourseImageUploader",
  components: { BIconCloudArrowUp, CropUploadedImage },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => ({
        id: null,
        url: null,
      }),
    },
    setCrop: {
      type: Boolean,
      default: false,
    },
    cropBoxRatio: {
      type: Number,
    },
    showSettings: {
      type: Boolean,
      default: false,
    },
    imgWidth: {
      type: Number,
      default: null
    },
    imgHeight: {
      type: Number,
      default: null
    },
  },
  data() {
    return {
      image: this.value,
      file: null,
      previewImgSrc: "",
    };
  },
  mounted() {
    if (this.showSettings) {
      this.image.settings ||= {
        width: 960,
        height: 470,
        label: null,
        alt: null,
      };
      this.$forceUpdate();
    }
  },
  methods: {
    chooseImage() {
      this.$refs.actualImageInput.click();
    },
    uploadBrowsedImage({ target }) {
      this.file = target.files ? target.files[0] : null;
      this.upload();
    },
    uploadImage(cropSettings = {}) {
      // TODO: при ошибке 500 падает скрипт и больше скрипты не работают.
      const imageSettings = { ...this.image.settings, ...cropSettings };
      this.$store.dispatch(PREVENT_UNLOAD);
      Api.multimedia.image
        .upload(this.file, { ...imageSettings, isInput: true })
        .then((response) => {
          if (!response.success) {
            throw new Error(response.error);
          }

          this.image = { ...response.data, settings: imageSettings };
          this.$emit("input", this.image);
          this.$emit("imageUploaded", this.image);
        })
        .catch((e) => {
          this.$alert.error("При загрузке файла произошла ошибка<br>" + e.message);
          this.image = {
            id: null,
            url: null,
          };
        })
        .finally(() => {
          this.$store.dispatch(ALLOW_UNLOAD);
        });
    },
    upload() {
      if (!this.file) {
        return;
      }

      if (this.setCrop) {
        this.previewImgSrc = URL.createObjectURL(this.file);
        this.$refs["CropUploadedImage"].showModal();
      } else {
        this.uploadImage();
      }
    },
    deleteImage() {
      this.image = {
        id: null,
        url: null,
      };
      this.$emit("input", this.image);
      this.$emit("imageDeleted");
    },
    onDragover(e) {
      e.target.classList.add("drag-over");
    },
    onDragLeave(e) {
      e.target.classList.remove("drag-over");
    },
    uploadDroppedImage(e) {
      this.onDragLeave(e);
      e.preventDefault();

      if (e.dataTransfer.items) {
        const file = e.dataTransfer.items[Object.keys(e.dataTransfer.items)[0]];
        if (file.kind === "file") {
          this.file = file.getAsFile();
          this.upload();
        }
      } else {
        this.$alert.error("Ошибка. Свяжитесь с разработчиками.");
      }
    },
  },
};
</script>
